import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger);

class Footer {
  private footer: HTMLElement;
  private footerLogo: HTMLElement;

  private blueSVG: SVGElement;
  private redSVG: SVGElement;
  private yellowSVG: SVGElement;
  private mouseX: number = window.innerWidth / 2;
  private mouseY: number = 0;
  private rafId: number | null = null;
  private split: SplitText | null = null;
  private letterSpans: HTMLElement[] = [];
  private letterData: { angle: number; distance: number; rotate: number }[] =
    [];

  constructor(
    footer: HTMLElement,
    footerLogo: HTMLElement,

    blueSVG: SVGElement,
    redSVG: SVGElement,
    yellowSVG: SVGElement
  ) {
    this.footer = footer;
    this.footerLogo = footerLogo;

    this.blueSVG = blueSVG;
    this.redSVG = redSVG;
    this.yellowSVG = yellowSVG;

    this.setPointer = this.setPointer.bind(this);
    this.animate = this.animate.bind(this);

    document.addEventListener("mousemove", this.setPointer);
    //document.addEventListener("touchmove", this.setPointer, {passive: false,});
    if (!this.rafId) {
      this.rafId = requestAnimationFrame(this.animate);
    }
  }

  // Helper to extract the current scale from the transform string

  private getCurrentTransform(el: SVGElement): any {
    const transform = el.style.transform;
    const result = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotate: 0,
    };

    if (!transform) return result;

    // Match scale
    const scaleMatch = /scale\(([^)]+)\)/.exec(transform);
    if (scaleMatch) result.scale = parseFloat(scaleMatch[1]);

    // Match translateX
    const txMatch = /translateX\(([^)]+)px\)/.exec(transform);
    if (txMatch) result.translateX = parseFloat(txMatch[1]);

    // Match translateY
    const tyMatch = /translateY\(([^)]+)px\)/.exec(transform);
    if (tyMatch) result.translateY = parseFloat(tyMatch[1]);

    // Match rotate
    const rotMatch = /rotate\(([^)]+)deg\)/.exec(transform);
    if (rotMatch) result.rotate = parseFloat(rotMatch[1]);

    return result;
  }

  private distanceFromBottom(y: number) {
    const rect = this.footer.getBoundingClientRect();
    return Math.max(0, rect.bottom - y);
  }

  // inverse normalized distance: 1 when near bottom, 0 when far (clamped)
  private distanceToBottom(y: number, maxDistance = 700) {
    const d = this.distanceFromBottom(y);
    const t = Math.min(d / maxDistance, 1);
    return 1 - t;
  }

  public init() {
    const EPSILON = 0.001;

    // --- ScrollTrigger sticky and fade ---
    gsap.set(this.footer, { position: "relative", zIndex: 10 });

    // --- Animate logo paths from scattered to assembled ---
    const logoPaths = this.footerLogo.querySelectorAll<SVGPathElement>("path");

    if (logoPaths.length > 0) {
      // Set initial scattered state for each path
      logoPaths.forEach((path) => {
        // Random scatter positions - smaller spread, only upward and sideways
        const randomX = (Math.random() - 0.5) * 150; // -75 to 75
        const randomY = -Math.random() * 200; // 0 to -200 (only upward, not below)
        const randomRotate = (Math.random() - 0.5) * 180; // -90 to 90 degrees
        const randomScale = 0.5 + Math.random() * 0.3; // 0.5 to 0.8

        gsap.set(path, {
          x: randomX,
          y: randomY,
          rotation: randomRotate,
          scale: randomScale,
          opacity: 0, // start completely invisible
          transformOrigin: "center center",
        });
      });

      // Animate to final assembled position on scroll with smooth easing like start-hero
      gsap.to(logoPaths, {
        x: 0,
        y: 0,
        rotation: 0,
        scale: 1,
        opacity: 1,
        duration: 1,
        ease: "power2.out", // smooth easing like start-hero
        stagger: 0.03, // tighter stagger for smoother feel
        scrollTrigger: {
          trigger: this.footer,
          start: "top bottom", // when footer enters viewport
          end: "top 60%",      // ends a bit later for slower animation
          scrub: 0.5,          // smoother scrub like start-hero
          markers: false,
        },
      });
    }

    ScrollTrigger.create({
      trigger: this.footer,
      start: "top top",

      scrub: true,
      onUpdate: (self) => {
        //console.log("On update: ", self.progress);
      },
      onLeave: () => {
        //gsap.set(this.letterSpans, { opacity: 0 });
      },
      onEnterBack: () => {
        //gsap.set(this.container, { opacity: 1 });
      },
    });
  }

  private setPointer(e: MouseEvent | TouchEvent) {
    if ("touches" in e && e.touches.length) {
      this.mouseX = e.touches[0].clientX;
      this.mouseY = e.touches[0].clientY;
    } else {
      this.mouseX = (e as MouseEvent).clientX;
      this.mouseY = (e as MouseEvent).clientY;
    }
  }

  private animate() {
    const rect = this.footer.getBoundingClientRect();
    const baseY = rect.bottom;

    let x = Math.max(rect.left, Math.min(this.mouseX, rect.right));
    let y = this.mouseY;

    // let distFromBottom = Math.max(0, baseY - y);
    // let t = Math.min(distFromBottom / 700, 1);

  
    let t = this.distanceToBottom(y, 700);

    let scale = 1 + t * 2;

    const offsetX = x - (rect.left + rect.width / 2);

    const blueBlur = 20 + t * 30;

    // Footer uses reversed logic (inverted with -scale-y-100)
    // t = 1 when mouse is FAR from footer top (at bottom of screen)
    // t = 0 when mouse is NEAR footer top (at top of screen)

    // Yellow: prominent when far (t=1), subtle when near (t=0) - REVERSED from hero
    const yellowScale = 0.02 + t * 0.58; // 0.02 when near top, 0.6 when far
    const yellowOpacity = 0.01 + t * 0.99; // 0.01 when near top, 1.0 when far
    const yellowBlur = 70 - t * 30; // more blur when near top

    // Red: prominent when near (t=0), subtle when far (t=1) - REVERSED from hero
    const redScale = 0.6 + (1 - t) * 0.8; // 1.4 when near top, 0.6 when far
    const redOpacity = 0.5 + (1 - t) * 0.5; // 1.0 when near top, 0.5 when far
    const redBlur = 25 + t * 55; // less blur when near top, very blurred when far

    // Blue scale: bigger when mouse far (t=1), smaller when near (t=0) - REVERSED from hero
    const blueScale = 1 + t * 1.1; // 1 when near top, 3.5 when far
    const blueOffsetY = 35 + (-t * 50); // Start at +35px, move up more when mouse is closer to footer

    gsap.to(this.blueSVG, {
      x: offsetX,
      y: blueOffsetY,
      scale: blueScale,
      transformOrigin: "50% 100%",
      filter: `blur(${blueBlur}px)`,
    });
    gsap.to(this.yellowSVG, {
      x: offsetX,
      scaleX: scale * yellowScale,
      scaleY: scale * 0.6,
      opacity: yellowOpacity,
      transformOrigin: "50% 100%",
      filter: `blur(${yellowBlur}px)`,
    });
    gsap.to(this.redSVG, {
      x: offsetX,
      scaleX: scale * redScale,
      scaleY: scale * 0.5,
      opacity: redOpacity,
      transformOrigin: "50% 100%",
      filter: `blur(${redBlur}px)`,
    });

    this.rafId = requestAnimationFrame(this.animate);
  }

  public destroy() {
    document.removeEventListener("mousemove", this.setPointer);
    document.removeEventListener("touchmove", this.setPointer);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }
}

// Only initialize if footer exists
function initFooter() {
  const footerElement = document.getElementById("footer");
  const footerLogo = document.getElementById("footerLogo");
  const blob = document.getElementById("footer-mouse-blob");

  if (!footerElement || !footerLogo || !blob) return;

  const [blueSVG, redSVG, yellowSVG] = Array.from(
    blob.querySelectorAll("svg")
  ) as SVGElement[];
  if (!blueSVG || !redSVG || !yellowSVG) return;

  const footer = new Footer(footerElement, footerLogo, blueSVG, redSVG, yellowSVG);
  footer.init();
}

// Auto-init on DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFooter);
} else {
  initFooter();
}
